<template>
  <b-card>
    <!-- Заголовок + назад + меню второстепенных действий -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="mb-25">
          {{ stageLabel }}
        </h4>
        <div
          v-if="stage"
          class="text-muted small"
        >
          Процесс: <b>{{ stage.proc_name }}</b> · инициатор: {{ stage.initiator }}
          <b-badge
            :variant="cubus.bpmStatusVariant(stage.status_code)"
            class="ml-50"
          >
            {{ cubus.bpmStatusLabel(stage.status_code) }}
          </b-badge>
          <b-badge
            v-if="stage.deadline"
            :variant="stage.overdue ? 'danger' : 'light-secondary'"
            class="ml-50"
          >
            срок: {{ stage.deadline }}{{ stage.overdue ? ' · просрочено' : '' }}
          </b-badge>
        </div>
      </div>
      <div class="d-flex">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-50"
          @click="$router.push({ name: 'cubus-bpm-inbox' })"
        >
          <feather-icon icon="ArrowLeftIcon" /> К задачам
        </b-button>
        <b-dropdown
          v-if="stage"
          size="sm"
          variant="outline-secondary"
          right
          no-caret
          text="Ещё"
        >
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" /> Ещё
          </template>
          <b-dropdown-item
            v-if="canClaim"
            @click="act('bpm_claim', 'Задача взята в работу')"
          >
            <feather-icon icon="UserCheckIcon" /> Взять в работу
            <small class="d-block text-muted">отметить, что задача у вас</small>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isInProgress"
            @click="act('bpm_unclaim', 'Задача возвращена в очередь')"
          >
            <feather-icon icon="CornerUpLeftIcon" /> Вернуть в очередь
            <small class="d-block text-muted">снять отметку «у вас»</small>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canSuspend"
            @click="act('bpm_suspend', 'Этап приостановлен')"
          >
            <feather-icon icon="PauseIcon" /> Приостановить
            <small class="d-block text-muted">отложить, не завершая</small>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isSuspended"
            @click="act('bpm_resume', 'Этап возобновлён')"
          >
            <feather-icon icon="PlayIcon" /> Возобновить
            <small class="d-block text-muted">вернуть в работу</small>
          </b-dropdown-item>
          <b-dropdown-divider v-if="canAbort" />
          <b-dropdown-item
            v-if="canAbort"
            variant="danger"
            @click="abort"
          >
            <feather-icon icon="XOctagonIcon" /> Прервать весь процесс
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Что нужно сделать -->
    <b-alert
      v-if="stage && (stage.descr || stage.instructions)"
      variant="primary"
      show
      class="mb-1"
    >
      <div class="p-1">
        <div
          v-if="stage.descr"
          class="font-weight-bold mb-25"
        >
          {{ stage.descr }}
        </div>
        <div
          v-if="stage.instructions"
          style="white-space: pre-line"
        >
          {{ stage.instructions }}
        </div>
      </div>
    </b-alert>

    <!-- Статусные подсказки -->
    <b-alert
      v-if="claimedByOther"
      variant="warning"
      show
      class="mb-1"
    >
      <div class="p-1">
        Задача в работе у <b>{{ stage.claimed_by }}</b> — завершить её может только он (или администратор).
      </div>
    </b-alert>
    <b-alert
      v-else-if="isSuspended"
      variant="secondary"
      show
      class="mb-1"
    >
      <div class="p-1">
        Этап приостановлен. Чтобы продолжить — «Ещё» → «Возобновить».
      </div>
    </b-alert>

    <!-- Исходные данные (пришли с прошлых шагов / от запуска) -->
    <template v-if="contextSlots.length">
      <h6 class="mt-1 mb-25">
        <feather-icon icon="InboxIcon" /> Исходные данные
        <small class="text-muted">— получено с прошлых шагов</small>
      </h6>
      <b-table
        small
        borderless
        :fields="[{ key: 'param_name', label: 'Поле' }, { key: 'value_json', label: 'Значение' }]"
        :items="contextSlots"
        class="mb-1"
      />
    </template>

    <!-- PROP-787: отметки чек-листа прошлых этапов — кто, когда, какой пункт -->
    <template v-if="prevChecks.length">
      <h6 class="mt-1 mb-25">
        <feather-icon icon="CheckSquareIcon" /> Проверки прошлых этапов
      </h6>
      <b-table
        small
        borderless
        data-bpm-prev-checks
        :fields="[{ key: 'stage_label', label: 'Этап' }, { key: 'item', label: 'Пункт' }, { key: 'checked', label: 'Отмечено' }, { key: 'user_name', label: 'Кто' }, { key: 'at', label: 'Когда' }]"
        :items="prevChecks"
        class="mb-1"
      >
        <template #cell(item)="d">
          {{ d.item.item }}<div
            v-if="d.item.descr"
            class="text-muted small"
          >{{ d.item.descr }}</div>
        </template>
        <template #cell(checked)="d">
          {{ d.item.checked ? '✓ да' : '✗ нет' }}
        </template>
      </b-table>
    </template>

    <!-- PROP-787: чек-лист согласующего — пункты проверки этапа; «Согласовать» — только когда отмечены все -->
    <template v-if="checkItems.length">
      <h6 class="mt-1 mb-25">
        <feather-icon icon="CheckSquareIcon" /> Проверьте
        <small class="text-muted">— для решения «Согласовать» отметьте все пункты</small>
      </h6>
      <b-form-checkbox
        v-for="op in checkItems"
        :key="op.id"
        v-model="outVals[op.param_name]"
        :data-bpm-check="op.param_name"
        class="mb-50"
        :disabled="!mayAct"
      >
        <b>{{ op.param_name }}</b><span
          v-if="op.required"
          class="text-danger"
        > *</span>
        <div
          v-if="op.descr"
          class="text-muted small"
        >
          {{ op.descr }}
        </div>
      </b-form-checkbox>
    </template>

    <!-- Заполните (выходные данные этапа) -->
    <template v-if="formParams.length">
      <h6 class="mt-1 mb-25">
        <feather-icon icon="Edit3Icon" /> Заполните
        <small class="text-muted">— эти данные пойдут дальше по процессу</small>
      </h6>
      <b-form-group
        v-for="op in formParams"
        :key="op.id"
        class="mb-50"
      >
        <template #label>
          {{ op.param_name }}<span v-if="op.required" class="text-danger"> *</span>
          <b-badge variant="light-secondary" class="ml-50">{{ typeLabel(op.datatype) }}</b-badge>
        </template>
        <!-- многострочный текст -->
        <b-form-textarea
          v-if="op.datatype === 'text'"
          v-model="outVals[op.param_name]"
          rows="3"
          size="sm"
          :disabled="!mayAct"
        />
        <!-- да / нет -->
        <b-form-checkbox
          v-else-if="op.datatype === 'boolean'"
          v-model="outVals[op.param_name]"
          :disabled="!mayAct"
        >
          Да
        </b-form-checkbox>
        <!-- файлы: множественная загрузка Excel; парсятся на бэке и кладутся в слот -->
        <div v-else-if="op.datatype === 'file'">
          <b-form-file
            multiple
            size="sm"
            :disabled="!mayAct || uploading"
            accept=".xlsx,.xls"
            placeholder="Выберите Excel-файлы (можно несколько)…"
            browse-text="Обзор"
            @input="onFile(op, $event)"
          />
          <small
            v-if="(fileVals[op.param_name] || []).length"
            class="text-muted d-block mt-25"
          >
            Выбрано файлов: {{ fileVals[op.param_name].length }} — будут загружены и разобраны при отправке.
          </small>
        </div>
        <!-- ссылка на объект Кубуса (фундамент: id записи; пикер записей — следующий шаг) -->
        <b-form-input
          v-else-if="op.datatype === 'ref'"
          v-model="outVals[op.param_name]"
          type="number"
          size="sm"
          placeholder="id записи объекта"
          :state="op.required ? !!(outVals[op.param_name] && String(outVals[op.param_name]).trim()) : null"
          :disabled="!mayAct"
        />
        <!-- строка / число / дата -->
        <b-form-input
          v-else
          v-model="outVals[op.param_name]"
          :type="op.datatype === 'number' ? 'number' : (op.datatype === 'date' ? 'date' : 'text')"
          size="sm"
          :state="op.required ? !!(outVals[op.param_name] && String(outVals[op.param_name]).trim()) : null"
          :disabled="!mayAct"
        />
      </b-form-group>
    </template>

    <!-- Открыть отдельную форму ввода (если привязана) -->
    <b-button
      v-if="stage && stage.form_obj_id"
      size="sm"
      variant="outline-info"
      class="mb-1"
      @click="$router.push({ name: 'cubus-object', params: { objectid: stage.form_obj_id } })"
    >
      <feather-icon icon="ExternalLinkIcon" /> Открыть форму ввода
    </b-button>

    <!-- Завершение этапа: главное действие -->
    <hr>
    <h6 class="mb-50">
      Завершите этап
    </h6>
    <div v-if="manualOutcomes.length && mayAct">
      <!-- PROP-770: комментарий к решению — в историю, письмо инициатору и слот outcome_comment -->
      <textarea
        v-model="comment"
        data-bpm-comment
        class="form-control mb-1"
        rows="2"
        maxlength="2000"
        placeholder="Комментарий к решению (увидит инициатор)"
      />
      <b-button
        v-for="(o, i) in manualOutcomes"
        :key="o.id"
        class="mr-1 mb-1"
        :data-bpm-outcome="o.id"
        :variant="i === 0 ? 'primary' : 'outline-primary'"
        :disabled="busy || !outputsReady || (approveLike(o) && !checksReady)"
        :title="approveLike(o) && !checksReady ? 'Отметьте все пункты проверки' : (Number(o.comment_required) === 1 ? 'Для этого решения нужен комментарий' : '')"
        @click="selectOutcome(o.id)"
      >
        <feather-icon icon="CheckIcon" /> {{ o.label }}<span v-if="Number(o.comment_required) === 1"> *</span>
      </b-button>
      <div
        v-if="!outputsReady"
        class="text-warning small mt-25"
      >
        Заполните обязательные поля (<span class="text-danger">*</span>), чтобы завершить этап.
      </div>
      <div
        v-else-if="!checksReady"
        class="text-warning small mt-25"
      >
        «Согласовать» станет доступно, когда отмечены все пункты проверки; вернуть или отклонить можно сразу.
      </div>
      <div
        v-else
        class="text-muted small mt-25"
      >
        Выберите результат — данные сохранятся автоматически.
      </div>
    </div>
    <div
      v-else-if="!manualOutcomes.length"
      class="text-muted"
    >
      У этого этапа нет кнопок завершения (он завершается автоматически).
    </div>
    <div
      v-else
      class="text-muted small"
    >
      Сейчас завершить этап нельзя ({{ cubus.bpmStatusLabel(status) }}).
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BBadge, BAlert, BFormGroup, BFormInput,
  BFormTextarea, BFormCheckbox, BFormFile,
  BDropdown, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'
import axiosIns from '@axios'

export default {
  name: 'CbsBpmTask',
  components: {
    BCard, BTable, BButton, BBadge, BAlert, BFormGroup, BFormInput,
    BFormTextarea, BFormCheckbox, BFormFile,
    BDropdown, BDropdownItem, BDropdownDivider,
  },
  data() {
    return {
      cubus: useCubus,
      stage: null,
      slots: [],
      outcomes: [],
      outparams: [],
      checks: [],
      outVals: {},
      fileVals: {},
      uploading: false,
      me: '',
      busy: false,
      comment: '',
    }
  },
  computed: {
    si() { return Number(this.$route.params.si) },
    stageLabel() { return (this.stage && this.stage.label) || 'Задача' },
    manualOutcomes() { return this.outcomes.filter(o => o.outcome_type !== 'auto') },
    status() { return (this.stage && this.stage.status_code) || '' },
    isInProgress() { return this.status === 'IN_PROGRESS' },
    isSuspended() { return this.status === 'SUSPENDED' },
    claimedByOther() { return this.stage && this.stage.claimed_by && this.stage.claimed_by !== this.me },
    canClaim() { return (this.status === 'ASSIGNED' || this.status === 'ACTIVE') && !this.claimedByOther },
    canSuspend() { return ['ACTIVE', 'ASSIGNED', 'IN_PROGRESS'].includes(this.status) && !this.claimedByOther },
    canAbort() { return this.stage && !['COMPLETED', 'ABORTED'].includes(this.status) },
    // можно ли действовать (заполнять/завершать): активный статус, не приостановлено, не занято другим
    mayAct() {
      return ['ACTIVE', 'ASSIGNED', 'IN_PROGRESS'].includes(this.status) && !this.claimedByOther
    },
    outparamNames() { return new Set(this.outparams.map(op => op.param_name)) },
    // исходные данные = заполненные слоты, которые НЕ являются выходными полями этого этапа
    contextSlots() { return this.slots.filter(s => !this.outparamNames.has(s.param_name)) },
    // PROP-787: пункты чек-листа (datatype check) — отдельно от полей ввода
    checkItems() { return this.outparams.filter(op => op.datatype === 'check') },
    formParams() { return this.outparams.filter(op => op.datatype !== 'check') },
    checksReady() { return this.checkItems.filter(op => op.required).every(op => this.outVals[op.param_name] === true) },
    prevChecks() { return this.checks.filter(c => Number(c.stage_instance_id) !== this.si) },
    outputsReady() {
      return this.formParams.filter(op => op.required)
        .every(op => this.outVals[op.param_name] != null && String(this.outVals[op.param_name]).trim() !== '')
    },
  },
  created() {
    useCubus.setPageTitle('Задача')
    this.load()
  },
  methods: {
    load() {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_task', param: { stage_instance_id: this.si }, mode: 'sync' },
      })
        .then(response => {
          if (response.data.error) { useCubus.toastError(this, response.data.error); return }
          const r = response.data.result || {}
          this.stage = r.stage || null
          this.slots = r.slots || []
          this.outcomes = r.outcomes || []
          this.outparams = r.outparams || []
          this.checks = r.checks || []
          const vals = {}
          this.outparams.forEach(op => {
            const v = op.value_json != null ? op.value_json : ''
            vals[op.param_name] = (op.datatype === 'boolean' || op.datatype === 'check') ? (v === 'true' || v === true) : v
          })
          this.outVals = vals
          this.me = r.me || ''
        })
        .catch(error => { useCubus.toastError(this, error) })
    },
    typeLabel(dt) {
      return {
        string: 'строка', text: 'текст', number: 'число', date: 'дата', boolean: 'да/нет', file: 'файл', ref: 'объект', check: 'пункт проверки',
      }[dt || 'string'] || 'строка'
    },
    // PROP-787: решение «согласовать» — вид approve или без вида, но не reject/return (как на бэке)
    approveLike(o) { return !['reject', 'return'].includes(String(o.outcome_kind || '').toLowerCase()) },
    onFile(op, files) {
      // b-form-file multiple → массив File; запоминаем файлы и показываем имена
      const arr = Array.isArray(files) ? files : (files ? [files] : [])
      this.$set(this.fileVals, op.param_name, arr)
      this.$set(this.outVals, op.param_name, arr.map(f => f.name).join('; '))
    },
    // загрузка Excel-файлов: парсятся на бэке (bpm_upload_file) и кладутся в слот
    async uploadFiles() {
      const token = useJwt.getCubusToken()
      const fileParams = this.outparams.filter(op => op.datatype === 'file' && (this.fileVals[op.param_name] || []).length)
      if (!fileParams.length) return true
      this.uploading = true
      try {
        for (const op of fileParams) {
          const files = this.fileVals[op.param_name]
          const fd = new FormData()
          const filekeys = files.map((f, i) => `file${i}`)
          files.forEach((f, i) => fd.append(`file${i}`, f))
          fd.append('query', JSON.stringify({
            method: 'bpm_upload_file',
            param: { stage_instance_id: this.si, param_name: op.param_name, filekeys },
            mode: 'sync',
          }))
          // eslint-disable-next-line no-await-in-loop
          const resp = await axiosIns.post(useJwt.jwtConfig.uploadEndpoint, fd, { headers: { Authorization: `Bearer ${token}` } })
          if (resp.data && resp.data.error) { useCubus.toastError(this, resp.data.error); this.uploading = false; return false }
        }
        // upload идёт в фоне (возвращает thread) — дождёмся, пока бэк разберёт файлы и заполнит слоты
        // eslint-disable-next-line no-await-in-loop
        await this.waitForFileSlots(fileParams.map(op => op.param_name))
      } catch (e) { useCubus.toastError(this, e); this.uploading = false; return false }
      this.uploading = false
      return true
    },
    async waitForFileSlots(names, tries = 14) {
      const token = useJwt.getCubusToken()
      for (let i = 0; i < tries; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await useJwt.query({ token, query: { method: 'bpm_task', param: { stage_instance_id: this.si }, mode: 'sync' } })
        const ops = ((resp.data || {}).result || {}).outparams || []
        const ready = names.every(n => { const op = ops.find(o => o.param_name === n); return op && op.value_json && String(op.value_json).trim() })
        if (ready) return true
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => { setTimeout(r, 500) })
      }
      return false
    },
    async saveOutputs() {
      // сначала грузим файлы (они заполняют слот на бэке), затем обычные поля
      const okUp = await this.uploadFiles()
      if (!okUp) return false
      const calls = this.outparams.filter(op => op.datatype !== 'file').map(op => useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_fill_output', param: { stage_instance_id: this.si, param_name: op.param_name, value: this.outVals[op.param_name] != null ? String(this.outVals[op.param_name]) : '' }, mode: 'sync' },
      }))
      const responses = await Promise.all(calls)
      const err = responses.map(r => r.data.error).find(Boolean)
      if (err) { useCubus.toastError(this, err); return false }
      return true
    },
    act(method, okMessage) {
      this.busy = true
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method, param: { stage_instance_id: this.si }, mode: 'sync' },
      })
        .then(response => {
          this.busy = false
          if (response.data.error) { useCubus.toastError(this, response.data.error); return }
          useCubus.toastMessage(this, okMessage)
          this.load()
        })
        .catch(error => { this.busy = false; console.log(`${method} error`, error) })
    },
    abort() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Прервать весь процесс? Действие необратимо.')) return
      this.busy = true
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_abort', param: { stage_instance_id: this.si }, mode: 'sync' },
      })
        .then(response => {
          this.busy = false
          if (response.data.error) { useCubus.toastError(this, response.data.error); return }
          useCubus.toastMessage(this, 'Процесс прерван')
          this.$router.push({ name: 'cubus-bpm-inbox' })
        })
        .catch(error => { this.busy = false; console.log('bpm_abort error', error) })
    },
    selectOutcome(outcomeId) {
      // PROP-770: решение с «комментарий обязателен» без комментария не отправляется (сервер отказал бы так же)
      const oc = this.outcomes.find(o => o.id === outcomeId)
      if (oc && Number(oc.comment_required) === 1 && !this.comment.trim()) {
        useCubus.toastError(this, `Для решения «${oc.label}» нужен комментарий — напишите, почему`)
        return
      }
      this.busy = true
      // сначала сохраняем заполненное (бэкенд не даст завершить без обязательных выходов)
      const pre = this.outparams.length ? this.saveOutputs() : Promise.resolve(true)
      pre.then(ok => {
        if (!ok) { this.busy = false; return }
        useJwt.query({
          token: useJwt.getCubusToken(),
          query: {
            method: 'bpm_outcome',
            param: { stage_instance_id: this.si, outcome_id: outcomeId, comment: this.comment.trim() },
            mode: 'sync',
          },
        })
          .then(response => {
            this.busy = false
            if (response.data.error) { useCubus.toastError(this, response.data.error); return }
            useCubus.toastMessage(this, 'Этап завершён')
            this.$router.push({ name: 'cubus-bpm-inbox' })
          })
          .catch(error => { this.busy = false; console.log('bpm_outcome error', error) })
      }).catch(() => { this.busy = false })
    },
  },
}
</script>
